import { RequestStatus } from 'features/request/requestSlice';
import type { BimWorkOrderOtherPriceData, WorkOrderStatusEnum } from 'gql/graphql';
import { BimEditOtherModal } from 'pages/Project/BimProject/BimCommonComponents/BimEditOtherModal';

import * as R from 'ramda';
import type React from 'react';
import { useCallback, useMemo } from 'react';

import { useBimMethodsEditOther } from './hooks/useBimMethodsEditOther';
import { useBimWorkOrderConstructionTypes } from './hooks/useBimWorkOrderConstructionTypes';

interface ICreateOtherModal {
    isOpenModal: boolean;
    constructionTypeId?: number;
    statusWorkOrder: WorkOrderStatusEnum;
    onCloseModal: () => void;
    refreshTable: () => void;
    dataRow?: BimWorkOrderOtherPriceData;
}

export const BimOtherPriceModal = ({
    dataRow,
    isOpenModal,
    constructionTypeId,
    statusWorkOrder,
    onCloseModal,
    refreshTable,
}: ICreateOtherModal) => {
    const resetIncomingData = useCallback(() => {
        refreshTable();
        onCloseModal();
    }, [onCloseModal, refreshTable]);

    const { bimWorkOrderConstructionTypesData } = useBimWorkOrderConstructionTypes();

    const {
        bimOtherData,
        bimWorkOrderOtherStatus,
        resetBimWorkOrderOtherData,
        bimUpdateWorkOrderOtherStatus,
        onUpdateOtherEdit,
    } = useBimMethodsEditOther({
        isOpenModal,
        dataRow,
        resetIncomingData,
        statusWorkOrder,
        bimWorkOrderConstructionTypes: bimWorkOrderConstructionTypesData,
    });

    const isUpdateFetching = useMemo(
        () => bimUpdateWorkOrderOtherStatus === RequestStatus.FETCHING,
        [bimUpdateWorkOrderOtherStatus],
    );

    const isLoading = useMemo(
        () => R.isNil(bimWorkOrderOtherStatus) || bimWorkOrderOtherStatus === RequestStatus.FETCHING,
        [bimWorkOrderOtherStatus],
    );

    const onSubmit = useCallback(
        (data) => {
            onUpdateOtherEdit(data);
        },
        [onUpdateOtherEdit],
    );

    return (
        <BimEditOtherModal
            otherData={bimOtherData}
            onSubmit={onSubmit}
            onCloseModal={onCloseModal}
            statusWorkOrder={statusWorkOrder}
            isDataLoading={isLoading}
            isUpdateInProgress={isUpdateFetching}
            constructionTypeId={constructionTypeId}
            constructionTypes={bimWorkOrderConstructionTypesData}
            resetOtherData={resetBimWorkOrderOtherData}
        />
    );
};

BimOtherPriceModal.displayName = 'BimOtherPriceModal';
